import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { DPE } from '../@types/DPE';
import { showToast } from '../components/common/Notification/';
import { fetchDpesData } from '../components/Prospecting/OwnerCommunication/utils/OwnerCommunicationService';

interface FilteredMonth {
  date: string;
  type?: 'reception' | 'etablissement';
}

interface DpesState {
  data: DPE[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  expandedRows: number;
  cloturerRows: number;
  showPhone: number[];
}

interface FetchDpesArgs {
  companyId: number;
  jwt: string;
  monthFiltered?: FilteredMonth
}

export const fetchTreatedDpes = createAsyncThunk<any[], FetchDpesArgs>(
  'dpeOwnerTreated/fetchDpes',
  async({ companyId, jwt, monthFiltered }) => {
    const response = await fetchDpesData(companyId, jwt, monthFiltered, true);
    return response;
  },
);

const initialState: DpesState = {
  data: [],
  status: 'idle',
  error: null,
  expandedRows: null,
  cloturerRows: null,
  showPhone: [],
};

const dpeOwnerTreatedSlice = createSlice({
  name: 'dpeOwnerTreated',
  initialState,
  reducers: {
    setExpandedRows(state, action: PayloadAction<number>) {
      state.expandedRows = action.payload;
    },
    setCloturerRows(state, action: PayloadAction<number>) {
      state.cloturerRows = action.payload;
    },
    setShowPhone(state, action: PayloadAction<number[]>) {
      state.showPhone = action.payload;
    },
    setContact(state, action: PayloadAction<any>) {
      // use action.payload.ownerId to add a new contact to the owner at the begginning of the contacts array
      state.data = state.data.map((dpe) => ({
        ...dpe,
        owners: dpe.owners.map((owner) => {
          if (owner.id !== action.payload.ownerId) {
            return owner;
          }
          return {
            ...owner,
            contacts: [
              {
                id: action.payload.contact.id,
                type: action.payload.contact.type,
                createdAt: action.payload.contact.createdAt,
                updatedAt: action.payload.contact.updatedAt,
              },
              ...owner.contacts,
            ],
          };
        }),
      }));
    },
    setInteraction(state, action: PayloadAction<{ ownerId: number }>) {
      // use action.payload.ownerId to add a new interaction to remove the owner from the list of owners to treat
      state.data = state.data.map((dpe) => ({
        ...dpe,
        owners: dpe.owners.filter((owner) => owner.id !== action.payload.ownerId),
      }));
      // check if the dpe is empty and remove it from the list if no more owners
      state.data = state.data.filter((dpe) => dpe.owners.length > 0);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTreatedDpes.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchTreatedDpes.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
        // reset expandedRows and cloturerRows when fetching new data
        state.expandedRows = null;
        state.cloturerRows = null;
      })
      .addCase(fetchTreatedDpes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
        showToast('error', 'Une erreur est survenue lors de la récupération des données.');
      });
  },
});

export const { setExpandedRows, setCloturerRows, setShowPhone, setContact, setInteraction } = dpeOwnerTreatedSlice.actions;

export default dpeOwnerTreatedSlice.reducer;
